import { CHARTS, dateOf, esc, lineChart, lineSwatch, money, SERIES, type Line } from './sbchart.ts';

export type EquityOwnerKind = 0 | 1 | 2 | 3;
export interface EquityIdentity { kind: EquityOwnerKind; id: number; name: string }
export interface EquityOwner extends EquityIdentity { shares: number; recorded?: boolean }
export interface EquityOwnersPage {
  schema: 'equity-owners-v1'; firm: number; day: number; since: number | null;
  frequency: 'month-end'; total: number; offset: number; limit: number;
  nextOffset: number | null; hasMore: boolean; owners: EquityOwner[];
}
export interface EquityPoint {
  day: number; shares: number | null; totalShares: number | null; pct: number | null;
  value: number | null; boughtFen: string | null; soldFen: string | null;
  dividendFen: string | null; priceSource: 'valuation' | 'unknown';
}
export interface EquityHistoryPage {
  schema: 'equity-history-v1'; firm: number; owner: EquityIdentity; day: number;
  since: number | null; frequency: 'month-end'; historyEnded: boolean;
  total: number; offset: number; limit: number; nextOffset: number | null;
  hasMore: boolean; points: EquityPoint[]; current: EquityPoint | null;
}
export interface EquityRankPage {
  schema: 'equity-rank-v1'; firm: number; requestedDay: number; day: number | null;
  current: boolean; metric: number; since: number | null; historyEnded: boolean;
  days: number[]; currentDay: number | null; total: number; offset: number; limit: number;
  nextOffset: number | null; hasMore: boolean; unrecorded: boolean;
  rows: { owner: EquityIdentity; point: EquityPoint | null }[];
}
export const EQUITY_RANK_CURRENT = 0xffffffff;
export const EQUITY_RANK_PAGE = 50;
export const EQUITY_METRICS = [
  ['pct', '占比'], ['shares', '股数'], ['value', '持股估值'],
  ['boughtFen', '累计投入'], ['soldFen', '累计回款'], ['dividendFen', '累计分红'],
] as const;
export type EquityMetric = typeof EQUITY_METRICS[number][0];
export const EQUITY_OWNER_PAGE = 50;
export const EQUITY_HISTORY_PAGE = 120;
export interface EquityOwnerStyle { color: string; dash?: readonly [number, number] }
export type EquityView = 'rank' | 'stack' | 'lines' | 'company';
export interface EquityCompanyPoint { day: number; marketValue: number | null; netAssets: number | null }
export interface EquityHistoryState {
  metric: EquityMetric; ownerOffset: number; historyOffset: number; initialized: boolean;
  selected: Map<string, EquityIdentity>; known: Map<string, EquityOwner>;
  styles: Map<string, EquityOwnerStyle>;
  view: EquityView; selectionMode: 'leaders' | 'fixed'; selectorOpen: boolean;
  directories: Map<number, EquityOwnersPage>; histories: Map<string, EquityHistoryPage>;
  timelineDay: number | null; playing: boolean; frameDays: number[];
  rankPage: EquityRankPage | null; rankOffset: number; rankUnsupported: boolean;
}
export const newEquityHistoryState = (): EquityHistoryState => ({
  metric: 'pct', ownerOffset: 0, historyOffset: 0, initialized: false,
  selected: new Map(), known: new Map(), styles: new Map(),
  view: 'rank', selectionMode: 'leaders', selectorOpen: false,
  directories: new Map(), histories: new Map(), timelineDay: null, playing: false, frameDays: [],
  rankPage: null, rankOffset: 0, rankUnsupported: false,
});
export const equityOwnerKey = (owner: Pick<EquityIdentity, 'kind' | 'id'>) => `${owner.kind}:${owner.id}`;
const ownerName = (owner: EquityIdentity) => `${owner.name} · ${['个人', '公司', '政府', '银行'][owner.kind]} #${owner.id}`;
const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const day = (n: unknown): n is number => finite(n) && Number.isInteger(n) && n >= 0 && n <= 0xffffffff;
const fen = (n: unknown): n is string => typeof n === 'string' && /^-?\d{1,40}$/.test(n);

/** 身份含类型，个人#2与公司#2永不合成一个股东。 */
export function equityOwnerColour(owner: EquityIdentity): string {
  const hash = (Math.imul(owner.id, 2654435761) + owner.kind * 17) >>> 0;
  return SERIES[hash % SERIES.length];
}
/** 按本公司首次选择分配；取消、重选和目录翻页不改已分配样式。 */
export function equityOwnerStyle(state: EquityHistoryState, owner: EquityIdentity): EquityOwnerStyle {
  const key = equityOwnerKey(owner);
  let style = state.styles.get(key);
  if (!style) {
    const n = state.styles.size, group = Math.floor(n / SERIES.length);
    style = { color: SERIES[n % SERIES.length], ...(group ? { dash: [2 + group * 2, 3] as const } : {}) };
    state.styles.set(key, style);
  }
  return style;
}
export function acceptEquityOwners(value: unknown): EquityOwnersPage | null {
  const p = value as EquityOwnersPage | null;
  return p?.schema === 'equity-owners-v1' && Array.isArray(p.owners) && day(p.firm)
    && p.owners.every(o => [0, 1, 2, 3].includes(o.kind) && day(o.id) && typeof o.name === 'string' && finite(o.shares)) ? p : null;
}
export function acceptEquityHistory(value: unknown, firm: number, owner: EquityIdentity): EquityHistoryPage | null {
  const p = value as EquityHistoryPage | null;
  return p?.schema === 'equity-history-v1' && p.firm === firm && p.owner != null
    && equityOwnerKey(p.owner) === equityOwnerKey(owner) && Array.isArray(p.points)
    && p.points.every(x => day(x.day)) && (p.current == null || day(p.current.day)) ? p : null;
}
export function acceptEquityRank(value: unknown, firm: number, requestedDay: number, metric: number, offset: number): EquityRankPage | null {
  const p = value as EquityRankPage | null;
  const identity = (o: EquityIdentity) => o && [0, 1, 2, 3].includes(o.kind) && day(o.id) && typeof o.name === 'string';
  return p?.schema === 'equity-rank-v1' && p.firm === firm && p.requestedDay === requestedDay
    && p.metric === metric && p.offset === offset && day(p.total) && day(p.limit) && p.limit > 0 && p.limit <= EQUITY_RANK_PAGE
    && (p.day === null || day(p.day)) && (p.currentDay === null || day(p.currentDay))
    && typeof p.current === 'boolean' && typeof p.unrecorded === 'boolean' && typeof p.historyEnded === 'boolean'
    && Array.isArray(p.days) && p.days.every((d, i) => day(d) && (i === 0 || p.days[i - 1] < d))
    && Array.isArray(p.rows) && p.rows.length <= p.limit && p.rows.every(r => identity(r.owner) && (r.point === null || day(r.point.day) && r.point.day === p.day))
    && new Set(p.rows.map(r => equityOwnerKey(r.owner))).size === p.rows.length
    && (p.nextOffset === null || day(p.nextOffset) && p.nextOffset > offset && p.nextOffset < p.total)
    && typeof p.hasMore === 'boolean' && p.hasMore === (p.nextOffset !== null) ? p : null;
}
export function rememberEquityRank(state: EquityHistoryState, page: EquityRankPage): void {
  const previous = state.rankPage;
  if (page.current && previous?.current && (page.day ?? 0) < (previous.day ?? 0)) return;
  // 日期轴停在当前帧时继续跟随；历史日期保持原日，不被新一天吸回上月。
  if (page.current && previous?.currentDay != null && state.timelineDay === previous.currentDay) state.timelineDay = page.currentDay;
  state.rankPage = page;
}
export function rememberEquityOwners(state: EquityHistoryState, page: EquityOwnersPage): void {
  if ((state.directories.get(page.offset)?.day ?? -1) > page.day) return;
  state.directories.set(page.offset, page);
  for (const o of page.owners) {
    const key = equityOwnerKey(o); state.known.set(key, o);
    if (state.selected.has(key)) state.selected.set(key, o);
  }
  if (page.offset === 0 && state.selectionMode === 'leaders') {
    // 目录按实际当前股数排序。翻到后面的目录不能替换主要股东。
    state.selected.clear();
    for (const o of page.owners.filter(o => o.shares > 0).slice(0, 5)) {
      state.selected.set(equityOwnerKey(o), o); equityOwnerStyle(state, o);
    }
    state.initialized = state.selected.size > 0;
  }
}
export function toggleEquityOwner(state: EquityHistoryState, key: string): void {
  state.selectionMode = 'fixed';
  if (state.selected.has(key)) state.selected.delete(key);
  else { const o = state.known.get(key); if (o) { state.selected.set(key, o); equityOwnerStyle(state, o); } }
}
export function followEquityLeaders(state: EquityHistoryState): void {
  state.selectionMode = 'leaders';
  const page = state.directories.get(0); if (page) rememberEquityOwners(state, page);
}
export function setEquityOwnersOpen(state: EquityHistoryState, open: boolean): void { state.selectorOpen = open; }
const historyKey = (key: string, offset: number) => `${key}@${offset}`;
export function rememberEquityHistory(state: EquityHistoryState, key: string, page: EquityHistoryPage): void {
  if (equityOwnerKey(page.owner) !== key) return;
  const k = historyKey(key, page.offset);
  if ((state.histories.get(k)?.day ?? -1) <= page.day) state.histories.set(k, page);
}
export function equityTimelineDays(state: EquityHistoryState): number[] { return [...state.frameDays]; }
export function setEquityTimelineDay(state: EquityHistoryState, requestedDay: number): void {
  if (!Number.isFinite(requestedDay) || !state.frameDays.length) return;
  let chosen = state.frameDays[0];
  for (const d of state.frameDays) { if (d > requestedDay) break; chosen = d; }
  state.timelineDay = chosen;
  state.rankOffset = 0;
}
export function stepEquityTimeline(state: EquityHistoryState, direction = 1): void {
  const days = state.frameDays; if (!days.length) return;
  const i = state.timelineDay == null ? days.length - 1 : Math.max(0, days.indexOf(state.timelineDay));
  state.timelineDay = days[Math.max(0, Math.min(days.length - 1, i + Math.sign(direction)))];
  state.rankOffset = 0;
}
export function toggleEquityPlayback(state: EquityHistoryState): void {
  if (state.playing) { state.playing = false; return; }
  if (state.frameDays.length < 2) return;
  if (state.timelineDay == null || state.timelineDay >= state.frameDays[state.frameDays.length - 1]) state.timelineDay = state.frameDays[0];
  state.playing = true;
}
export function advanceEquityTimeline(state: EquityHistoryState): boolean {
  if (!state.playing || state.view !== 'rank') { state.playing = false; return false; }
  stepEquityTimeline(state);
  if (state.timelineDay === state.frameDays[state.frameDays.length - 1]) state.playing = false;
  return true;
}

/** 末值/浮框保留分币；Number只用于SVG位置和坐标轴，不回写记录。 */
export function equityFenText(value: string | null): string {
  if (!fen(value)) return '未记录';
  const n = BigInt(value), a = n < 0n ? -n : n;
  return `${n < 0n ? '−' : ''}${(a / 100n).toLocaleString('en-US')}.${(a % 100n).toString().padStart(2, '0')} 元`;
}
export function equitySharesText(value: number | null): string {
  if (!finite(value)) return '未记录';
  if (value === 0) return '0 股';
  return `${new Intl.NumberFormat('zh-CN', { maximumSignificantDigits: 15 }).format(value)} 股`;
}
export function equityMetricNumber(p: EquityPoint | null, metric: EquityMetric): number {
  if (!p) return NaN;
  if (metric === 'pct') return finite(p.shares) && finite(p.totalShares) && p.totalShares > 0 ? p.shares / p.totalShares * 100 : NaN;
  if (metric === 'shares' || metric === 'value') return finite(p[metric]) ? p[metric] : NaN;
  return fen(p[metric]) ? Number(p[metric]) / 100 : NaN;
}
export function equityMetricText(p: EquityPoint | null, metric: EquityMetric): string {
  if (!p) return '未记录';
  if (metric === 'shares') return equitySharesText(p.shares);
  if (metric === 'pct') {
    const n = equityMetricNumber(p, metric);
    return finite(n) ? `${new Intl.NumberFormat('zh-CN', { maximumSignificantDigits: 15 }).format(n)}%` : '未记录';
  }
  if (metric === 'value') return finite(p.value) ? money(p.value) : '未记录';
  return equityFenText(p[metric]);
}

export interface EquityChartRow { day: number; current: boolean; gap: boolean }
export interface EquityChartModel { rows: EquityChartRow[]; lines: Line[] }
/** 只画本页真实观察点；停止历史不与多年后的当前读数相连。 */
export function equityChartModel(pages: EquityHistoryPage[], metric: EquityMetric, offset: number, state?: EquityHistoryState): EquityChartModel {
  const days = [...new Set(pages.flatMap(p => p.points.map(x => x.day)))].sort((a, b) => a - b);
  const rows: EquityChartRow[] = [];
  for (const d of days) {
    if (rows.length && d - rows[rows.length - 1].day > 30) rows.push({ day: rows[rows.length - 1].day + 1, gap: true, current: false });
    rows.push({ day: d, gap: false, current: false });
  }
  const currents = offset === 0 ? pages.filter(p => !p.historyEnded && p.current).map(p => p.current!) : [];
  const now = currents.length ? Math.max(...currents.map(p => p.day)) : null;
  if (now != null && (days.length === 0 || now >= days[days.length - 1])) {
    if (days.length && now - days[days.length - 1] > 30) rows.push({ day: days[days.length - 1] + 1, gap: true, current: false });
    // 同一日也保留月末样本和当前读数两个独立点，不覆盖已记录的名册。
    rows.push({ day: now, gap: false, current: true });
  }
  const lines = pages.map(p => {
    const at = new Map(p.points.map(x => [x.day, x]));
    const points = rows.map(r => r.gap ? null : r.current ? (!p.historyEnded && p.current?.day === r.day ? p.current : null) : at.get(r.day) ?? null);
    const style = state ? equityOwnerStyle(state, p.owner) : { color: equityOwnerColour(p.owner) };
    return { name: ownerName(p.owner), ...style,
      vals: points.map(x => equityMetricNumber(x, metric)), texts: points.map(x => equityMetricText(x, metric)) };
  });
  return { rows, lines };
}

/** 日常标签简短；浮框仍用实际分母和完整分币。 */
export function equityMetricShortText(p: EquityPoint | null, metric: EquityMetric): string {
  if (metric !== 'pct') return equityMetricText(p, metric);
  const n = equityMetricNumber(p, metric); return finite(n) ? `${Number(n.toFixed(2))}%` : '未记录';
}
const pointOn = (p: EquityHistoryPage, d: number, current: boolean) => current
  ? (!p.historyEnded && p.current?.day === d ? p.current : null) : p.points.find(x => x.day === d) ?? null;
export interface EquityRankRow { owner: EquityIdentity; value: number; text: string; exact: string }
/** 排行只使用该日真实观察，不把上一个月的数量冒作中间某一天。 */
export function equityRankRows(pages: EquityHistoryPage[], metric: EquityMetric, d: number, current = false): EquityRankRow[] {
  return pages.flatMap(p => {
    const q = pointOn(p, d, current), value = equityMetricNumber(q, metric);
    return finite(value) ? [{ owner: p.owner, value, text: equityMetricShortText(q, metric), exact: equityMetricText(q, metric) }] : [];
  }).sort((a, b) => b.value - a.value || a.owner.kind - b.owner.kind || a.owner.id - b.owner.id);
}
export interface EquityStackRow extends EquityChartRow { values: number[] | null; other: number | null; totalShares: number | null }
/** 其他=实际总股数减所选股数；分母/任一所选数量未知、冲突或缺页时整列断开。 */
export function equityStackModel(pages: EquityHistoryPage[], expectedOwners = pages.length, offset = 0): EquityStackRow[] {
  const model = equityChartModel(pages, 'pct', offset);
  return model.rows.map(r => {
    const ps = pages.map(p => pointOn(p, r.day, r.current));
    const total = ps[0]?.totalShares;
    const known = !r.gap && pages.length === expectedOwners && pages.length > 0 && finite(total) && total > 0
      && ps.every(p => p && finite(p.shares) && p.shares >= 0 && p.totalShares === total);
    const sum = known ? ps.reduce((n, p) => n + p!.shares!, 0) : NaN;
    if (!known || sum > total! + Math.max(1, total!) * 1e-10) return { ...r, values: null, other: null, totalShares: null };
    return { ...r, values: ps.map(p => p!.shares! / total! * 100), other: Math.max(0, (total! - sum) / total! * 100), totalShares: total! };
  });
}
function stackHTML(firm: number, state: EquityHistoryState, pages: EquityHistoryPage[]): string {
  const rows = equityStackModel(pages, state.selected.size, state.historyOffset);
  if (!rows.some(r => r.values)) return '<div class="sb-empty">占比记录尚未齐全</div>';
  const W = 560, H = 180, L = 36, R = 10, T = 12, B = 26;
  const first = rows[0].day, span = rows[rows.length - 1].day - first || 1;
  const x = (r: EquityStackRow) => L + (W - L - R) * (r.day - first) / span;
  const y = (v: number) => T + (H - T - B) * (1 - v / 100);
  let svg = `<svg class="sb-chart" data-equity-stack="${firm}" viewBox="0 0 ${W} ${H}">`;
  for (const v of [0, 25, 50, 75, 100]) svg += `<line class="g" x1="${L}" x2="${W-R}" y1="${y(v)}" y2="${y(v)}"/><text x="${L-4}" y="${y(v)+3}" text-anchor="end">${v}%</text>`;
  // 单点仍画窄竖条；连续缺口分段，不用0填空后接起来。
  const segments: EquityStackRow[][] = [[]];
  for (const r of rows) { if (r.values) segments[segments.length - 1].push(r); else if (segments[segments.length - 1].length) segments.push([]); }
  const parts: { name: string; style: EquityOwnerStyle }[] = [...pages.map(p => ({ name: p.owner.name, style: equityOwnerStyle(state, p.owner) })), { name: '其他股东', style: { color: 'var(--ink-3)' } }];
  for (const seg of segments.filter(s => s.length)) for (let k = 0; k < parts.length; k++) {
    const bounds = seg.map(r => {
      const vals = [...r.values!, r.other!], low = vals.slice(0, k).reduce((a, b) => a + b, 0);
      return { r, low, high: low + vals[k] };
    });
    if (seg.length === 1) {
      const b = bounds[0]; svg += `<rect x="${x(b.r)-2}" y="${y(b.high)}" width="4" height="${Math.max(0,y(b.low)-y(b.high))}" style="fill:${parts[k].style.color}"/>`;
    } else svg += `<polygon points="${bounds.map(b => `${x(b.r)},${y(b.high)}`).join(' ')} ${[...bounds].reverse().map(b => `${x(b.r)},${y(b.low)}`).join(' ')}" style="fill:${parts[k].style.color}" opacity=".8"/>`;
  }
  for (const r of rows.filter(r => r.values)) {
    const exact = pages.map((p, i) => `${p.owner.name}: ${r.values![i]}%`).concat(`其他股东: ${r.other}%`).join('；');
    svg += `<rect x="${x(r)-3}" y="${T}" width="6" height="${H-T-B}" fill="transparent"><title>${esc(dateOf(r.day)+(r.current?' · 当前只读':'')+'；'+exact)}</title></rect>`;
  }
  svg += `<text x="${L}" y="${H-5}">${esc(dateOf(first))}</text><text x="${W-R}" y="${H-5}" text-anchor="end">${esc(dateOf(rows[rows.length-1].day))}</text></svg>`;
  return `<div class="sb-legend">${parts.map(p => `<span>${lineSwatch(p.style.color, p.style.dash)}${esc(p.name)}</span>`).join('')}</div>`+svg;
}
function rankHTML(firm: number, state: EquityHistoryState, loading: boolean): string {
  const page = state.rankPage;
  if (state.rankUnsupported) return '<div class="sb-empty">此引擎没有全体股东排行</div>';
  if (!page) return '<div class="sb-empty">' + (loading ? '正在读取股东排行' : '尚未记录历史') + '</div>';
  if (page.unrecorded || page.day == null) return '<div class="sb-empty">这一天未记录</div>';
  const metric = EQUITY_METRICS[page.metric][0];
  const rows = page.rows.map(r => ({ ...r, value: equityMetricNumber(r.point, metric) }));
  const max = rows.reduce((m, r) => finite(r.value) ? Math.max(m, Math.abs(r.value)) : m, 0) || 1;
  // 服务端按当日全体名册排序；金额按整数分排序，前端只画位置，不用Number重排。
  const body = rows.map((r, i) => {
    const style = equityOwnerStyle(state, r.owner);
    const value = finite(r.value) ? r.value : 0;
    const width = metric === 'pct' ? Math.max(0, Math.min(100, value)) : Math.abs(value) / max * 100;
    return '<div data-equity-rank-owner="' + equityOwnerKey(r.owner) + '" title="' + esc(ownerName(r.owner) + ' · ' + equityMetricText(r.point, metric)) + '" style="position:absolute;left:0;right:0;top:0;transform:translateY(' + i * 32 + 'px);transition:transform .4s;display:grid;grid-template-columns:minmax(90px,1fr) 2fr auto;gap:8px;align-items:center;height:32px">'
      + '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + (page.offset + i + 1) + '. ' + esc(r.owner.name) + '</span><span style="display:block;height:15px;background:var(--line);border-radius:3px"><span style="display:block;height:100%;width:' + width + '%;background:' + style.color + ';border-radius:3px;transition:width .4s"></span></span><b>' + esc(equityMetricShortText(r.point, metric)) + '</b></div>';
  }).join('');
  const attr = 'data-equity-firm="' + firm + '"';
  const prev = page.offset > 0 ? '<button ' + attr + ' data-equity-rank-page="' + Math.max(0, page.offset - EQUITY_RANK_PAGE) + '">上一页</button>' : '';
  const next = page.nextOffset != null ? '<button ' + attr + ' data-equity-rank-page="' + page.nextOffset + '">下一页</button>' : '';
  return '<div data-equity-rank="' + firm + '"><div class="sb-row"><b>' + esc(dateOf(page.day)) + '</b><span class="meta">' + (page.current ? '当前 · ' : '') + '全部股东 · ' + page.total + ' · ' + EQUITY_METRICS[page.metric][1] + '</span></div><div data-equity-rank-rows="' + firm + '" style="position:relative;height:' + Math.max(1, rows.length) * 32 + 'px">' + (body || '<div class="sb-empty">这一天未记录</div>') + '</div><div data-equity-rank-paging>' + prev + ' ' + next + '</div></div>';
}
function companyHTML(firm: number, points: EquityCompanyPoint[]): string {
  const rows = points.filter(p => day(p.day)).slice().sort((a,b) => a.day-b.day);
  if (rows.length < 2) return '<div class="sb-empty">公司月结记录不足</div>';
  return lineChart(rows.map(p => p.day), [{ name:'总市值', vals:rows.map(p => finite(p.marketValue)?p.marketValue:NaN), fmt:money },
    { name:'净资产', vals:rows.map(p => finite(p.netAssets)?p.netAssets:NaN), fmt:money }], { key:`equity-company:${firm}`,fmt:money,zero:true,label:dateOf,annualDelta:false });
}
function linesHTML(firm: number, state: EquityHistoryState, pages: EquityHistoryPage[], model: EquityChartModel, loading: boolean): string {
  const recorded = new Set(pages.flatMap(p => p.points.map(x => x.day))).size;
  if (!state.selected.size) return '<div class="sb-empty">选择股东看曲线</div>';
  if (!pages.length) return `<div class="sb-empty">${loading?'正在读取月末记录':'尚未记录历史'}</div>`;
  if (!recorded) return '<div class="sb-empty">此前未记录</div>';
  if (model.rows.length < 2) return '<div class="sb-empty">仅 1 个记录点</div>';
  const key = `equity:${firm}:${state.metric}:${state.historyOffset}`, fmt = state.metric === 'pct' ? (x:number)=>`${Number(x.toFixed(2))}%` : state.metric === 'shares' ? (x:number)=>equitySharesText(x) : money;
  let chart = lineChart(model.rows.map(x=>x.day),model.lines,{h:140,fmt,zero:true,key,label:dateOf,annualDelta:false,
    rows:i=>[[model.rows[i].current?'当前只读点':model.rows[i].gap?'记录缺口':'首记 / 月末记录',dateOf(model.rows[i].day)]]});
  if (state.metric === 'pct') {
    const cut = chart.indexOf('<svg'), legend = chart.slice(0,cut);
    let compact = legend;
    for (const l of model.lines) { const n=l.vals[l.vals.length-1]; if (finite(n)) compact=compact.replace(`<b>${esc(l.texts![l.texts!.length-1])}</b>`,`<b>${Number(n.toFixed(2))}%</b>`); }
    chart = compact + chart.slice(cut);
  }
  const registered=CHARTS.get(key), currentIndex=model.rows.findIndex(x=>x.current);
  if (registered?.ys && currentIndex>=0) {
    const x=registered.L+(registered.W-registered.L-registered.R)*currentIndex/(model.rows.length-1);
    const rings=model.lines.map(l=>finite(l.vals[currentIndex])?`<circle cx="${x.toFixed(1)}" cy="${registered.ys!(l.vals[currentIndex]).toFixed(1)}" r="4" style="fill:var(--card);stroke:${l.color}" stroke-width="1.8"/>`:'').join('');
    chart=chart.replace('<g class="hov">',`${rings}<g class="hov">`);
  }
  return chart;
}

/** 相同公司/城市的有效页在state中保留；刷新等待期间不撤掉控制栏或已画曲线。 */
export function equityHistoryHTML(firm: number, state: EquityHistoryState, directory: EquityOwnersPage | null,
  histories: Map<string, EquityHistoryPage | null>, loading = false, companyTotals: EquityCompanyPoint[] = []): string {
  if (directory?.firm === firm) rememberEquityOwners(state,directory);
  for (const [key,p] of histories) if (p?.firm === firm) rememberEquityHistory(state,key,p);
  directory=state.directories.get(state.ownerOffset)??null;
  const selected=[...state.selected.entries()], pages=selected.flatMap(([key])=>{const p=state.histories.get(historyKey(key,state.historyOffset));return p?[p]:[];});
  const model=equityChartModel(pages,state.metric,state.historyOffset,state);
  state.frameDays=state.view==='rank' ? [...new Set([...(state.rankPage?.days ?? []), ...(state.rankPage?.currentDay != null ? [state.rankPage.currentDay] : [])])].sort((a,b)=>a-b) : [...new Set(model.rows.filter(r=>!r.gap).map(r=>r.day))];
  if (state.timelineDay!=null && !state.frameDays.includes(state.timelineDay)) setEquityTimelineDay(state,state.timelineDay);
  const attr=`data-equity-firm="${firm}"`, company=state.view==='company', hide=(on:boolean)=>on?' hidden':'';
  const views=`<div class="sb-tabs" data-equity-views>${([['rank','排行'],['stack','占比总览'],['lines','普通曲线'],['company','公司总量']] as const).map(([v,n])=>`<button ${attr} data-equity-view="${v}" class="${state.view===v?'on':''}" aria-pressed="${state.view===v}">${n}</button>`).join('')}</div>`;
  const controls=`<div class="sb-tabs" data-equity-metrics${hide(company||state.view==='stack')}>${EQUITY_METRICS.map(([k,n])=>`<button ${attr} data-equity-metric="${k}" class="${state.metric===k?'on':''}" aria-pressed="${state.metric===k}">${n}</button>`).join('')}</div>`;
  const names=selected.map(([key,o])=>{const st=equityOwnerStyle(state,o);return `<button ${attr} data-equity-owner="${key}" aria-pressed="true" title="移除此曲线" class="chip on">${lineSwatch(st.color,st.dash)} ${esc(o.name)} ×</button>`;}).join('');
  const choice=`<details data-key="equity-owners:${firm}"${state.selectorOpen?' open':''}><summary>选择股东 · ${selected.length} / ${directory?.total??'—'}</summary><div>${directory?.owners.map(o=>{const key=equityOwnerKey(o);return `<button ${attr} data-equity-owner="${key}" aria-pressed="${state.selected.has(key)}" class="chip${state.selected.has(key)?' on':''}">${esc(ownerName(o))} · ${equitySharesText(o.shares)}</button>`;}).join('')??'<span class="meta">正在读取股东</span>'}</div><div>${state.ownerOffset>0?`<button ${attr} data-equity-page="${Math.max(0,state.ownerOffset-EQUITY_OWNER_PAGE)}">上一页</button>`:''} ${directory?.nextOffset!=null?`<button ${attr} data-equity-page="${directory.nextOffset}">更多股东</button>`:''}</div></details>`;
  const selection=`<div data-equity-selection${hide(company||state.view==='rank')}><div><button ${attr} data-equity-follow class="${state.selectionMode==='leaders'?'on':''}" aria-pressed="${state.selectionMode==='leaders'}">跟随主要股东</button> <span class="meta">${state.selectionMode==='leaders'?'当前前5名':'手动固定'}</span></div><div data-equity-selected>${names}</div>${choice}</div>`;
  const days=state.frameDays, at=state.timelineDay??days[days.length-1], disabled=days.length<2?' disabled':'';
  const timeline=`<div data-equity-timeline${hide(state.view!=='rank')}><div class="sb-tabs"><button ${attr} data-equity-step="-1"${disabled}>‹</button><button ${attr} data-equity-play aria-pressed="${state.playing}"${disabled}>${state.playing?'暂停':'播放'}</button><button ${attr} data-equity-step="1"${disabled}>›</button><span>${at!=null?esc(dateOf(at)):'尚未记录'}</span></div><input ${attr} data-equity-time type="range" min="${days[0]??0}" max="${days[days.length-1]??0}" step="1" value="${at??0}" aria-label="股东历史日期" style="width:100%"${disabled}></div>`;
  let chart=company?companyHTML(firm,companyTotals):state.view==='stack'?stackHTML(firm,state,pages):state.view==='rank'?rankHTML(firm,state,loading):linesHTML(firm,state,pages,model,loading);
  const currentIndex=model.rows.findIndex(x=>x.current), chartDrawn=state.view==='lines'&&chart.includes('<svg class="sb-chart"');
  const current=company||state.view==='stack'||state.view==='rank'? '':pages.filter((p,i)=> {
    const shownInRank = state.view==='rank' && !p.historyEnded && p.current?.day===at && finite(equityMetricNumber(p.current,state.metric));
    const shownInLine = chartDrawn && state.historyOffset===0 && !p.historyEnded && currentIndex===model.rows.length-1
      && currentIndex>=0 && p.current?.day===model.rows[currentIndex].day && finite(model.lines[i].vals[currentIndex]);
    return !(shownInRank || shownInLine);
  })
    .map(p=>`<div class="sb-row" data-equity-current="${equityOwnerKey(p.owner)}"><span>${esc(p.owner.name)} · 当前</span><b title="${esc(equityMetricText(p.current,state.metric))}">${esc(equityMetricShortText(p.current,state.metric))}</b></div>`).join('');
  const next=pages.map(p=>p.nextOffset).filter((n):n is number=>n!=null), since=directory?.since??pages.find(p=>p.since!=null)?.since;
  return `<div data-equity-root="${firm}">${views}${controls}${selection}${timeline}<div data-equity-chart>${chart}</div><div data-equity-current-rows${hide(company)}>${current}</div><div data-equity-paging${hide(company||state.view==='rank')}>${state.historyOffset>0?`<button ${attr} data-equity-window="${Math.max(0,state.historyOffset-EQUITY_HISTORY_PAGE)}">较近记录</button>`:''} ${next.length?`<button ${attr} data-equity-window="${Math.min(...next)}">更早记录</button>`:''}</div><p class="meta">${company?'公司月结':'首记 / 月末'}${!company&&since!=null?` · 从${dateOf(since)}起`:''}${!company&&state.historyOffset?` · 较早时段 #${state.historyOffset}`:!company&&currentIndex>=0?' · ○ 当前':''}${!company&&pages.some(p=>p.historyEnded)?' · 已停止记录':''}${loading?' · 更新中':''} <span title="持股估值按估值价。持股账累计投入、回款、分红；含继承或合并转入，不等于本人的现金流水或持仓成本。记录起点前及未知金额不补零。">ⓘ</span></p></div>`;
}
